import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { IconButton } from "../primitives/IconButton";
import { CollapsibleSidebar } from "./CollapsibleSidebar";
import { EntityWorkspaceSidebar } from "./EntityWorkspaceSidebar";
import { useWorkspaceSidebarState } from "./useWorkspaceSidebarState";

type SidebarItem = {
  id: string;
  label: string;
  icon: string;
  active?: boolean;
  onClick?: () => void;
};

type EntityWorkspaceProps = {
  topbar?: ReactNode;
  header?: ReactNode;
  sidebarTitle: string;
  sidebarIcon: string;
  sidebarMeta?: ReactNode;
  sidebarItems: SidebarItem[];
  renderSidebar?: (isExpanded: boolean) => ReactNode;
  content: ReactNode;
  storageKey?: string;
  defaultCollapsed?: boolean;
  defaultPinned?: boolean;
  allowHoverOpen?: boolean;
};

export function EntityWorkspace({
  topbar,
  header,
  sidebarTitle,
  sidebarIcon,
  sidebarMeta,
  sidebarItems,
  renderSidebar,
  content,
  storageKey = "xlflow.entity.sidebar",
  defaultCollapsed = false,
  defaultPinned = true,
  allowHoverOpen = false,
}: EntityWorkspaceProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarState = useWorkspaceSidebarState({
    collapsedKey: `${storageKey}.collapsed`,
    pinnedKey: `${storageKey}.pinned`,
    defaultCollapsed,
    defaultPinned,
  });

  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const update = () => {
      setIsMobile(media.matches);
      if (media.matches) {
        setMobileOpen(false);
      }
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5">
      {topbar ? (
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border pb-3">
          <div className="flex items-center gap-2">
            {isMobile ? (
              <IconButton
                icon="≡"
                ariaLabel="Ouvrir le menu"
                intent="ghost"
                size="sm"
                onClick={() => setMobileOpen(true)}
              />
            ) : null}
            {topbar}
          </div>
        </div>
      ) : null}
      {header}
      <div className="grid min-h-0 flex-1 gap-6" style={{ gridTemplateColumns: isMobile ? "1fr" : "min-content 1fr" }}>
        {!isMobile ? (
          <div className="min-h-0 flex flex-col overflow-hidden">
            <CollapsibleSidebar
            title={sidebarTitle}
            headerIcon={sidebarIcon}
            headerMeta={sidebarMeta}
            pinned={sidebarState.pinned}
            collapsed={sidebarState.collapsed}
            onToggleCollapsed={sidebarState.toggleCollapsed}
            onTogglePinned={sidebarState.togglePinned}
            allowHoverOpen={allowHoverOpen}
          >
            {({ isExpanded }) =>
              renderSidebar ? renderSidebar(isExpanded) : <EntityWorkspaceSidebar items={sidebarItems} isExpanded={isExpanded} />
            }
          </CollapsibleSidebar>
          </div>
        ) : null}
        <section className="min-h-0 min-w-0 overflow-auto">{content}</section>
      </div>
      <CollapsibleSidebar
        isMobile
        isOpen={mobileOpen}
        onOpenChange={setMobileOpen}
        title={sidebarTitle}
        headerIcon={sidebarIcon}
        headerMeta={sidebarMeta}
        pinned={sidebarState.pinned}
        collapsed={sidebarState.collapsed}
        onToggleCollapsed={sidebarState.toggleCollapsed}
        onTogglePinned={sidebarState.togglePinned}
      >
        {({ isExpanded }) =>
          renderSidebar ? renderSidebar(isExpanded) : <EntityWorkspaceSidebar items={sidebarItems} isExpanded={isExpanded} />
        }
      </CollapsibleSidebar>
    </div>
  );
}
